import { Location, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import {
    CommonMessages,
    CookieService,
    HtmlNode,
    LoadingIndicatorHandler,
    LoadingIndicatorService,
    LoginNavigationService,
    MessageLifetime,
    MessageQueueService,
    MessageType,
    NativeAppService,
    NavigationService,
    Page,
    PlainLinkComponent,
    TrackingService,
    UserLoginEvent,
    UserService,
    ViewTemplateForClient,
    WindowRef,
} from '@frontend/vanilla/core';
import { LhHeaderBarComponent } from '@frontend/vanilla/features/header-bar';
import { MessagePanelComponent } from '@frontend/vanilla/features/message-panel';
import { NavigationLayoutPageComponent } from '@frontend/vanilla/features/navigation-layout';
import { TrustAsHtmlPipe } from '@frontend/vanilla/shared/browser';
import {
    DateComponent,
    DateRangeValidatorDirective,
    FormFieldComponent,
    ValidationConfig,
    ValidationMessagesComponent,
} from '@frontend/vanilla/shared/forms';
import { RouteDataService } from '@frontend/vanilla/shared/routing';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { CommonClientConfig, ZendeskClientConfig } from '../client-config/client-config.models';
import { PortalConstService } from '../client-config/client-constants';
import { PORTAL_FORMS_COMPONENTS } from '../forms/forms-component';
import { NewAccountMenuService } from '../services/account-menu-v2.service';
import { PortalNavigationService } from '../services/portal-navigation.service';
import { ScrollToService } from '../services/scrollTo.service';
import { CommonMethodsService } from '../tracking/common-methods-service';
import { PortalTrackingService } from '../tracking/tracking-resource.service';
import { ZendeskLiveChatComponent } from '../zendesk-live-chat/zendesk-live-chat.component';
import { ContactResourceService } from './contact-resource.service';
import {
    Categories,
    CategoryItem,
    CategoryTemplate,
    ContactData,
    ContactInitDataResponse,
    HelpAndContactData,
    LinkTemplateItem,
} from './contact.models';
import { FocusContactPageComponent } from './focus-contact-page';
import { HelpAndContactComponent } from './help-and-contact.component';
import { ObjectLengthPipe } from './object-length.pipe';
import { ContactOptionsComponent } from './parts/contact-options.component';
import { EmailOptionComponent } from './parts/email-option.component';

@Component({
    selector: 'pt-contact',
    templateUrl: 'contact.component.html',
    styleUrls: ['../../styles/portal/contact-page.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        FocusContactPageComponent,
        LhHeaderBarComponent,
        NgTemplateOutlet,
        EmailOptionComponent,
        NavigationLayoutPageComponent,
        MessagePanelComponent,
        FormsModule,
        NgClass,
        FormFieldComponent,
        ValidationMessagesComponent,
        DateComponent,
        DateRangeValidatorDirective,
        ...PORTAL_FORMS_COMPONENTS,
        PlainLinkComponent,
        ZendeskLiveChatComponent,
        ContactOptionsComponent,
        HelpAndContactComponent,
        TrustAsHtmlPipe,
        ObjectLengthPipe,
    ],
})
export class ContactComponent implements OnInit, OnDestroy {
    @ViewChild('uploadFile') selectedImageFile: any;
    @ViewChild('contactForm') currentForm: NgForm;
    @ViewChild('dateofbirth') dateofbirth: ElementRef;
    initData: ContactInitDataResponse;
    wait: boolean;
    content: ViewTemplateForClient;
    categoriesList: CategoryItem[];
    userAgent: string;
    deviceType: string;
    isUsnjLabel: boolean;
    additionalTextContent: any;
    contactInfoText: any;
    contactInfoTextCountries: any;
    geoCountry: string;
    faqLink: LinkTemplateItem | null;
    docUploadLink: LinkTemplateItem | null;
    twitterLink: LinkTemplateItem | null;
    faqPageLinks: LinkTemplateItem[];
    renderCategories: any;
    contactPageVisible: boolean;
    data: ContactData;
    screenshotPreviewVisible: any;
    currentFaqItem: any;
    selectedCategory: any | null = null;
    subCategory: any | null = null;
    imageData: any;
    hasAddedPicture: boolean;
    faqItems: any;
    disableSubCategory: boolean;
    subCategories: CategoryTemplate[];
    ACCEPT_TYPES: string;
    portrait: any;
    hideLiveChat: boolean = false;
    enableRefactoredChat: boolean;
    contactPageLinks: LinkTemplateItem[];
    isShown: boolean;
    selectedItem: any = null;
    subCategoryList: any[] = [];
    contentFaq: any = null;
    currentSelectedCategory: any = null;
    currentSelectedCategoryId: any = null;
    currentSelectedSubCatergory: any = null;
    selectedSubCategory: boolean = false;
    categorySelected: boolean = false;
    optionsScreen: boolean = false;
    helpAndContactData: HelpAndContactData;
    showEmail: boolean = false;
    showPhone: boolean = false;
    emailScreenContactData: any;
    phoneScreenContactData: any;
    screenName: string;
    countriesList: any[];
    newsubCategory: any;
    categoriesListItems: any;
    currentSelectedSubCatergoryList: any;
    continueButtonHide: boolean = false;
    usePortalDobComponent: boolean = false;
    phase3: boolean = false;
    zendeskChatEnabled: boolean;
    removeLiveChat: boolean;
    private loadingIndicatorHandler: LoadingIndicatorHandler;
    private unsubscribe = new Subject<void>();
    showchathistory: boolean = false;
    redirectedFromSASWFlow: boolean = false;
    showEmailForm: boolean = false;
    constructor(
        private routeData: RouteDataService,
        public validation: ValidationConfig,
        public commonMessages: CommonMessages,
        private contactResourceService: ContactResourceService,
        private nativeApplication: NativeAppService,
        private user: UserService,
        private tracking: TrackingService,
        private location: Location,
        private navigation: NavigationService,
        private loginNavigationService: LoginNavigationService,
        private loadingIndicator: LoadingIndicatorService,
        private messageQueue: MessageQueueService,
        private htmlNode: HtmlNode,
        private windowRef: WindowRef,
        private scrollService: ScrollToService,
        private activatedRoute: ActivatedRoute,
        public newAccountMenuService: NewAccountMenuService,
        private cookieService: CookieService,
        private zendeskClientConfig: ZendeskClientConfig,
        private router: Router,
        private commonMethodsService: CommonMethodsService,
        private commonClientConfig: CommonClientConfig,
        private portalTrackingService: PortalTrackingService,
        private page: Page,
        private portalNavigation: PortalNavigationService,
    ) {
        this.helpAndContactData = new HelpAndContactData();
        if (this.commonClientConfig.shouldReuseRoute) this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    }

    ngOnInit() {
        if (document.getElementById('hcc-skeleton')) document.getElementById('hcc-skeleton')!.remove();
        this.newAccountMenuService.scrollToTop();
        if (this.navigation.location.search.has('source')) {
            const source = this.navigation.location.search.get('source') || '';
            this.redirectedFromSASWFlow = source == 'sasw' ? true : false;
            this.clickEventTrackingFromFooterOrMedallia(source);
        }
        if (!this.contactResourceService.guideRouteValidation()) return;
        this.zendeskChatEnabled = this.zendeskClientConfig.enabled;
        this.removeLiveChat = this.zendeskClientConfig.removeLiveChat;
        if (this.removeLiveChat) this.portalTrackingService.trackingEventsZendeskService('click', 'contact us');
        if (this.user.geoCountry) this.geoCountry = this.user.geoCountry.toLowerCase();
        this.initData = this.routeData.getInitData();
        this.usePortalDobComponent = this.initData.isNewDobEnabled;
        this.phase3 = this.initData.enableHelpAndContactPhase3;
        this.tracking.triggerEvent('Event.HCC', {
            'component.CategoryEvent': 'hcc',
            'component.LabelEvent': this.phase3 ? 'new' : 'old',
            'component.ActionEvent': 'load',
            'component.PositionEvent': 'not applicable',
            'component.LocationEvent': 'not applicable',
            'component.EventDetails': 'not applicable',
            'component.URLClicked': 'not applicable',
        });
        this.initContent();
        this.categoriesListItems = this.initData.useNewHelpAndContactTemplate ? this.initData.newCategoriesList : this.initData.oldCategoriesList;
        if (!this.initData.enableHelpAndContactPhase2) {
            this.optionsScreen = this.initData.isVipUser;
            if (this.navigation.location.search.has('source')) {
                if (
                    this.navigation.location.search.get('source') === 'documentupload' ||
                    this.navigation.location.search.get('source') === 'mohinterceptor'
                ) {
                    this.optionsScreen = true;
                } else if (this.navigation.location.search.get('source') === 'registration') {
                    this.categoriesListItems
                        .filter((category: any) => category.id === 'accountaccess')
                        .map((category: any) => {
                            category.checked = true;
                            this.selectedCategoryList(category);
                        });
                }
            }
            this.resetScreenshot();
            this.updateFaqItems();
            this.prefillData();
        }
        const guideEnabled = this.commonMethodsService.getGuideConfig()?.guideEnabled;
        if (guideEnabled && this.navigation.location.search.has('z-showoptions') && this.navigation.location.search.get('z-showoptions') === 'true') {
            this.optionsScreen = true;
        }
        if (this.navigation.location.search.has('trackingId') && this.navigation.location.search.get('trackingId') == 'realitycheckpreferences')
            this.tracking.triggerEvent('Event.Functionality.Gambling', {
                'page.referringAction': 'GamblingControl_TimeManagement_ContactUs_Clicked',
                'page.siteSection': 'Account',
            });
        else if (this.navigation.location.search.has('source') && this.navigation.location.search.get('source') == 'registration')
            this.tracking.triggerEvent('Event.Clicks', {
                'component.CategoryEvent': 'Registration',
                'component.LabelEvent': 'Spanish Registration-Account creation error message',
                'component.ActionEvent': 'Customer Support link-Clicked',
                'component.PositionEvent': 'not applicable',
                'component.LocationEvent': 'Registration-Error Message',
            });
        if (
            !this.optionsScreen &&
            this.categoriesListItems !== undefined &&
            this.categoriesListItems.length > 0 &&
            !this.initData.isVipUser &&
            !this.initData.enableHelpAndContactPhase2
        ) {
            this.capturePageLoadTracking('Help_Contact_HP_nonVIP_Section1');
            this.screenName = 'Section1';
        }
        if (this.initData.enableHelpAndContactPage || this.initData.enableHelpAndContactPhase2) {
            this.user.events
                .pipe(
                    first((e) => e instanceof UserLoginEvent),
                    takeUntil(this.unsubscribe),
                )
                .subscribe(() => {
                    this.contactResourceService.getPostLoginRedirectOverrideContidionUsnj().subscribe((response) => {
                        const isOverrideenWorkflow: boolean = response;
                        if (
                            this.navigation.location.absUrl().indexOf(this.portalNavigation.normalizeUrl('/contact')) > 0 &&
                            isOverrideenWorkflow &&
                            this.user.workflowType == 0
                        )
                            this.windowRef.nativeWindow.location.reload();
                    });
                });
        }
        if (this.activatedRoute.snapshot.queryParamMap.get('showContact') == 'true' && this.activatedRoute.snapshot.queryParamMap.get('keyword')) {
            if (!this.isUserAuthenticated() && this.initData.loginKeywords.indexOf(this.activatedRoute.snapshot.queryParamMap.get('keyword')!) > -1)
                this.loginNavigationService.goToLogin({ forceReload: true, cancelUrl: this.page.homePage, appendReferrer: window.location.href });
            else this.optionsScreen = true;
        }
        if (this.initData.enableChatHistory && this.initData.chatHistoryContent && this.initData.chatHistory != null) {
            this.user.events
                .pipe(
                    first((e) => e instanceof UserLoginEvent),
                    takeUntil(this.unsubscribe),
                )
                .subscribe(() => {
                    const chatStatus = JSON.parse(this.initData.chatHistory.toString())[this.initData.playerPriority.toLowerCase()];
                    chatStatus && chatStatus.toLowerCase() == 'on' && this.newAccountMenuService.getMenuVersion() != 1
                        ? (this.showchathistory = true)
                        : (this.showchathistory = false);
                });
            this.tracking.triggerEvent('Event.HCC', {
                'component.CategoryEvent': 'hcc',
                'component.LabelEvent': 'chat history',
                'component.ActionEvent': 'load',
                'component.PositionEvent': 'suggestions',
                'component.LocationEvent': 'homepage',
                'component.EventDetails': 'chat history option',
                'component.URLClicked': 'not applicable',
                'component.hccCategory': 'not applicable',
                'component.hccArticle': 'not applicable',
            });
        }
        if (this.navigation.location.search.get('email') == 'true') {
            this.showEmailForm = true;
            const dataToSend: any = {};
            dataToSend.contactData = this.initData;
            this.emailScreenContactData = dataToSend;
        }
    }
    category_selected() {
        this.subCategory = null;
        this.subCategories = [];
        this.disableSubCategory = false;
        if (this.selectedCategory) {
            for (const categoryItem in this.selectedCategory.children) {
                this.subCategories.push(this.selectedCategory.children[categoryItem] as CategoryTemplate);
            }
            const keys = Object.keys(this.selectedCategory.children);
            if (keys.length === 1) {
                this.subCategory = this.selectedCategory.children[keys[0]] as CategoryTemplate;
                this.disableSubCategory = true;
            }
        }
        this.updateFaqItems();
    }

    contact() {
        const dataToSend = this.prepareDataToSend();
        this.wait = true;
        let trackingPage = this.location.path();
        if (dataToSend.category !== null || dataToSend.subCategory !== null) trackingPage += '/' + dataToSend.category + '_' + dataToSend.subCategory;
        this.contactResourceService.submit(dataToSend).subscribe(
            () => {
                this.contactPageVisible = false;
                this.wait = false;
                this.newAccountMenuService.scrollToTop();
                this.tracking.triggerEvent('Event.ContactPage', { 'page.siteSection': 'ContactMailSend', 'page.name': trackingPage });
            },
            (rejection: any) => {
                this.wait = false;
                const errorCode = rejection && rejection.errorCode ? rejection.errorCode : '';
                this.tracking.updateDataLayer({ 'page.name': trackingPage });
                this.tracking.reportError({ message: 'Technical error', code: errorCode });
            },
        );
    }

    isUserAuthenticated() {
        return this.user.isAuthenticated;
    }

    gotoHome() {
        if (this.nativeApplication.isNativeApp) {
            const lastKnownProduct: any = this.cookieService.get('lastKnownProduct');
            lastKnownProduct && lastKnownProduct.includes('thegrid')
                ? this.navigation.goToLastKnownProduct()
                : this.loginNavigationService.goToHome();
        } else if (this.windowRef.nativeWindow.history.length < 3) {
            this.navigation.goToLastKnownProduct();
        } else {
            this.navigation.goToLastKnownProduct({ forceHistoryBack: true });
        }
    }

    gotoLastKnownProduct(sectionName: string = '', action: string = '') {
        const vipStatus = this.initData.isVipUser ? 'VIP' : 'nonVIP';
        if (this.optionsScreen && this.initData.enableHelpAndContactPage && this.initData.isVipUser) {
            sectionName = 'Options';
        }
        if (sectionName === 'FAQ' && action === 'NO') {
            this.captureHelpAndContactTracking(`${sectionName}_${this.currentSelectedSubCatergory}_${action}`);
        } else {
            if (sectionName !== '') {
                this.captureEventTracking(`Help_Contact_HP_${vipStatus}_${sectionName}_${action}_Clicked`);
            }
        }
        if (!this.initData.isVipUser && action === 'Back' && this.navigation.location.search.get('source') != 'mohinterceptor') {
            if (this.helpAndContactData.showButtons) {
                this.optionsScreen = false;
                this.helpAndContactData.showButtons = false;
                if (this.currentSelectedCategoryId !== null) {
                    this.categoriesListItems.map((category: any) => {
                        category.checked = category.id === this.currentSelectedCategoryId;
                    });
                    this.subCategoryList.map((subCategory: any) => {
                        if (this.initData.useNewHelpAndContactTemplate) {
                            subCategory.checked = subCategory.value.metadata.id.itemName === this.currentSelectedSubCatergory;
                        } else {
                            subCategory.checked = subCategory.key === this.currentSelectedSubCatergory;
                        }
                    });
                }
                return;
            }
        }
        if (
            this.navigation.location.search.has('source') &&
            (this.navigation.location.search.get('source') === 'documentupload' || this.navigation.location.search.get('source') === 'interceptor')
        ) {
            this.navigation.goToLastKnownProduct({ forceHistoryBack: false });
        } else if (this.navigation.location.search.get('source') === 'mohinterceptor') {
            if (this.nativeApplication.isNativeApp) {
                this.loginNavigationService.goToHome();
            } else this.navigation.goToLastKnownProduct();
        } else {
            this.navigation.goToLastKnownProduct({ forceHistoryBack: true });
        }
    }
    //TODO: (refactoring) extract image upload outside of contact.component (can use upload.component)
    imageError(fileReaderError: any) {
        this.htmlNode.setCssClass('no-scrolling', false);
        this.wait = false;
        this.messageQueue.clear();
        const message = fileReaderError ? this.content.messages!.ScreenshotNotReadable : this.content.messages!.ScreenshotNotSupported;
        const buttonLabel = this.hasAddedPicture ? this.content.form.changescreenshot.label : this.content.form.addscreenshot.label;
        this.messageQueue.add(message.replace('{0}', buttonLabel as any), MessageType.Error, MessageLifetime.Single);
        this.loadingIndicatorHandler.done();
    }

    imagePicked(file: any) {
        this.wait = true;
        this.imageData.Name = file.fileName;
        this.loadingIndicatorHandler = this.loadingIndicator.start();
    }

    imageLoaded(imageData: any) {
        this.imageData.ContentType = imageData.mimeType;
        this.imageData.Data = imageData.data;
        this.hasAddedPicture = true;
        this.messageQueue.clear();
        this.wait = false;
        this.loadingIndicatorHandler.done();
    }

    screenshotPreview(value: any) {
        this.screenshotPreviewVisible = value;
    }

    thumbnailUpdated() {
        const thumbnailImg: HTMLImageElement | null = document.querySelector('#contactPageContainer .thumb img');
        if (thumbnailImg) this.portrait = thumbnailImg['naturalHeight'] > thumbnailImg['naturalWidth'];
    }

    updateFaqItems() {
        if (this.activatedRoute.snapshot.queryParamMap.get('display') === 'form') {
            this.faqItems = null;
            return;
        }
        this.faqItems = null;
        const dataToSend = new Categories();
        if (this.selectedCategory) {
            dataToSend.categoryId = this.selectedCategory.id;
            if (this.subCategory) {
                dataToSend.subCategoryId = this.subCategory.id;
            }
        }
        this.contactResourceService.getFaqItems(dataToSend).subscribe((data: any) => {
            this.faqItems = data.items.length ? data.items : null;
        });
    }

    resetScreenshot() {
        this.imageData = {};
        this.hasAddedPicture = false;
        if (this.selectedImageFile) {
            this.selectedImageFile.nativeElement.value = '';
        }
    }

    openFaqItem(item: any, event: Event) {
        if (item !== this.currentFaqItem) {
            this.currentFaqItem = item;
            // fix scrolling (issue with scroll position with dynamically inserted content)
            const element = event.target as HTMLElement;
            if (element) {
                const previousOffset = element.offsetTop - window.scrollY;
                this.windowRef.nativeWindow.scrollTo(0, element.offsetTop - previousOffset);
            }
        } else {
            this.currentFaqItem = null;
        }
    }

    forceContactForm() {
        this.faqItems = null;
        this.newAccountMenuService.scrollToTop();
    }
    selectedCategoryList(selectedItem: any) {
        this.scrollService.scrollTo('#Category', 500, -130);
        this.categorySelected = true;
        if (selectedItem.id !== this.currentSelectedCategoryId) {
            this.subCategoryList = [];
            this.selectedSubCategory = false;
            this.subCategory = selectedItem.category;
            this.newsubCategory = selectedItem.category;
            this.currentSelectedCategory = selectedItem.category;
            this.currentSelectedCategoryId = selectedItem.id;
            if (!this.initData.useNewHelpAndContactTemplate && this.subCategory) {
                Object.keys(this.subCategory.children).map((name) => {
                    const objectCategory: { [key: string]: string } = {};
                    objectCategory['key'] = name;
                    objectCategory['title'] = this.subCategory.children[name].title;
                    this.subCategoryList.push(objectCategory);
                });
            } else {
                this.newsubCategory.childs.map((child: any) => {
                    const objectCategory: { [key: string]: string } = {};
                    objectCategory['value'] = child;
                    objectCategory['title'] = child.faqTitle;
                    this.subCategoryList.push(objectCategory);
                });
            }
        }
        this.capturePageLoadTracking('Help_Contact_HP_nonVIP_Section2');
        this.captureHelpAndContactTracking(`WCWH_${this.currentSelectedCategoryId}`);
        this.screenName = 'Section2';
    }
    selectedSubCategoryList(selectedSubCategory: any) {
        this.scrollService.scrollTo('#SubCategory', 500, -130);
        if (!this.initData.useNewHelpAndContactTemplate) {
            this.currentSelectedSubCatergory = selectedSubCategory.key;
        } else {
            this.currentSelectedSubCatergory = selectedSubCategory.value.metadata.id.itemName;
            this.currentSelectedSubCatergoryList = selectedSubCategory;
        }
        if (!this.initData.useNewHelpAndContactTemplate) {
            const dataToSend: any = {};
            dataToSend.subCategoryId = selectedSubCategory.key;
            dataToSend.categoryId = this.currentSelectedCategoryId;
            this.contactResourceService.getSubCategoryContent(dataToSend).subscribe((data: any) => {
                this.selectedSubCategory = true;
                this.contentFaq = data;
            });
        } else {
            this.selectedSubCategory = true;
        }
        this.capturePageLoadTracking('Help_Contact_HP_nonVIP_Section3');
        this.captureHelpAndContactTracking(`FAQ_${this.currentSelectedSubCatergory}`);
        this.screenName = 'Section3';
    }
    goToOptions() {
        this.captureHelpAndContactTracking(`FAQ_${this.currentSelectedSubCatergory}_YES`);
        this.screenName = 'Section4_YES';
        this.optionsScreen = true;
    }
    enableEmailScreen(showEmail: boolean) {
        this.showEmail = showEmail;
    }
    disableEmailScreen(goToContactOptionScreen: boolean) {
        this.showEmail = false;
        this.optionsScreen = goToContactOptionScreen;
        this.helpAndContactData.showEmailScreen = false;
        this.helpAndContactData.showButtons = true;
    }
    emailScreenLoad(data: any) {
        this.emailScreenContactData = data;
    }

    enablePhoneScreen(data: any) {
        this.phoneScreenContactData = data;
        this.showPhone = data.showPhone;
    }

    disablePhoneScreen(showPhone: boolean) {
        this.showPhone = showPhone;
        this.optionsScreen = true;
        this.helpAndContactData.showButtons = false;
    }

    faqLinkClick(el: any) {
        if (el.target.nodeName.toLowerCase() === 'a') {
            this.captureEventTracking('Help_Contact_HP_nonVIP_Section3_Link_Clicked');
        }
    }

    getEmailAddress(message: string) {
        this.data.email = message;
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    goToHistory() {
        this.tracking.triggerEvent('Event.HCC', {
            'component.CategoryEvent': 'hcc',
            'component.LabelEvent': 'chat history',
            'component.ActionEvent': 'click',
            'component.PositionEvent': 'suggestions',
            'component.LocationEvent': 'homepage',
            'component.EventDetails': 'chat history option',
            'component.URLClicked': 'not applicable',
            'component.hccCategory': 'not applicable',
            'component.hccArticle': 'not applicable',
        });
        this.portalNavigation.navigate('/chathistory');
    }

    private prefillData() {
        if (this.user) {
            this.data.email = this.user.email;
            this.data.emailAutoFilled = this.user.email;
            this.data.deviceType = this.deviceType ? this.deviceType : null;
            this.data.deviceTypeAutoFilled = this.data.deviceType;
        }
        if (this.isUsnjLabel) {
            this.data.firstName = this.user.firstName;
            this.data.firstNameAutoFilled = this.user.firstName;
            this.data.surname = this.user.lastName;
            this.data.surnameAutoFilled = this.user.lastName;
            const birthDate = new Date(this.user.dateOfBirth!.getTime() + 60 * 60 * 1000 * 14);
            if (birthDate && !isNaN(birthDate.getTime()) && Object.prototype.toString.call(birthDate) === '[object Date]') {
                this.data.birthData = birthDate;
            }
        }
    }

    private prepareDataToSend() {
        const dataToSend = this.data;
        dataToSend.deviceType = this.data.deviceType;
        dataToSend.deviceTypeAutoFilled = this.data.deviceTypeAutoFilled;
        //Selected category and subCategory could be null on premium.com for anonymous users [B-152086]
        dataToSend.category = this.selectedCategory ? this.selectedCategory.title : null;
        dataToSend.subCategory = this.subCategory ? this.subCategory.title : null;
        if (this.selectedCategory && this.selectedCategory.messages && this.selectedCategory.messages.SupportEmailAddress_Override) {
            dataToSend.supportEmailAddress = this.selectedCategory.messages.SupportEmailAddress_Override;
        } else {
            if (this.content.messages) {
                dataToSend.supportEmailAddress = this.content.messages.SupportEmailAddress;
            }
        }
        dataToSend.userAgent = this.userAgent;
        // add optional screenshot
        if (this.hasAddedPicture) {
            dataToSend.screenshot = this.imageData;
        }
        return dataToSend;
    }

    private getLinkByKey(links: LinkTemplateItem[], key: string): LinkTemplateItem | null {
        const filteredLinks = links ? links.filter((l) => l.key === key) : null;
        return filteredLinks && filteredLinks.length ? filteredLinks[0] : null;
    }

    private initContent() {
        this.initData.documentUploadContent.messages.UploadDocumentNotSupported = this.initData.clientContentData.messages.UploadDocumentNotSupported;
        this.content = this.initData.clientContentData;
        this.categoriesList = this.initData.categoriesList;
        this.userAgent = this.initData.userAgent;
        this.deviceType = this.initData.deviceType;
        this.additionalTextContent = this.initData.additionalTextContent;
        this.contactInfoText = this.initData.contactInfoText;
        this.contactInfoTextCountries = this.initData.contactInfoTextCountries;
        this.faqLink = this.getLinkByKey(this.initData.contactPageLinkButtons, 'faq');
        this.docUploadLink = this.getLinkByKey(this.initData.contactPageLinkButtons, 'docupload');
        this.twitterLink = this.getLinkByKey(this.initData.contactPageLinkButtons, 'twitter');
        this.faqPageLinks = this.initData.faqPageLinks;
        this.renderCategories = this.initData.renderCategories;
        this.contactPageLinks = this.initData.contactPageLinks;
        this.isUsnjLabel = this.initData.isUSNJLabel;
        this.contactPageVisible = true;
        this.data = new ContactData();
        this.data.birthData = null;
        this.subCategory = null;
        this.ACCEPT_TYPES = this.initData.allowedFileTypes;
        this.screenshotPreviewVisible = false;
        this.currentFaqItem = null;
        // To check
        Object.keys(this.categoriesList).map((key: any) => {
            const categoryItem = this.categoriesList[key];
            categoryItem.category.id = categoryItem.key;
        });
        //fields should be required if it is a usnj label or the user is not logged in
        if (this.isUsnjLabel || (this.user && !this.user.isAuthenticated && this.user.workflowType === 0)) {
            this.isShown = true;
        }
        //should not show continue button for usnj poker native apps as it redirects to default casino page
        if (this.nativeApplication.product === PortalConstService.POKER && this.nativeApplication.isNativeApp && this.isUsnjLabel) {
            this.continueButtonHide = true;
        }
        this.orderCountries();
    }

    private orderCountries() {
        if (this.initData.contactInfoTextCountries && this.initData.contactInfoTextCountries.children) {
            this.countriesList = Object.keys(this.initData.contactInfoTextCountries.children)
                .map((x) => {
                    return {
                        code: x,
                        name: this.initData.contactInfoTextCountries.children[x].title,
                    };
                })
                .sort((a, b) => (a.name > b.name ? 1 : -1));
        }
    }

    private captureEventTracking(eventName: string) {
        this.tracking.triggerEvent('Event.Functionality.HelpContact', { 'page.referringAction': eventName });
    }
    private captureHelpAndContactTracking(eventName: string) {
        this.tracking.triggerEvent('Event.HelpAndContact', { 'page.HelpAndContact': eventName });
    }

    private capturePageLoadTracking(eventName: string) {
        this.tracking.triggerEvent('pageView', { 'page.name': eventName });
    }

    private clickEventTrackingFromFooterOrMedallia(source: string | null): void {
        const commonData = {
            'component.CategoryEvent': 'hcc',
            'component.LabelEvent': 'contact us',
            'component.ActionEvent': 'click',
            'component.PositionEvent': this.windowRef.nativeWindow.document.referrer,
            'component.URLClicked': this.windowRef.nativeWindow.location.href,
            'component.hccCategory': 'not applicable',
            'component.hccArticle': 'not applicable',
        };
        switch (source) {
            case 'footer':
                this.tracking.triggerEvent('Event.HCC', {
                    'component.LocationEvent': 'Help & Contact',
                    'component.EventDetails': 'footer - entry point',
                    ...commonData,
                });
                break;
            case 'medallia':
                this.tracking.triggerEvent('Event.HCC', {
                    'component.LocationEvent': 'medallia',
                    'component.EventDetails': 'medallia - entry point',
                    ...commonData,
                });
                break;
            case 'header':
                this.tracking.triggerEvent('Event.HCC', {
                    'component.LocationEvent': 'Help & Contact',
                    'component.EventDetails': 'header - entry point',
                    ...commonData,
                });
                break;
        }
    }
}
